import { useState, useRef, useCallback, useEffect } from 'react';
import ReactCrop, { Crop, PixelCrop, convertToPixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { adminButton } from '@/components/admin/ui';
import { cropToImageRect } from '@/lib/logo-crop';

interface LogoCropperProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageSrc: string;
  onCropComplete: (croppedBlob: Blob) => void;
}

// Saved logo height in pixels: the site shows logos up to ~84px tall, so 240px
// stays sharp on high-density (2x/3x) screens.
const OUTPUT_HEIGHT = 240;


/** The whole picture: the starting box, so nothing is cut off until the owner chooses. */
const FULL_CROP: Crop = { unit: '%', x: 0, y: 0, width: 100, height: 100 };

/**
 * Draw what's inside the crop box onto a canvas `height` pixels tall, at the
 * box's own shape. Parts of the box outside the picture (zoomed out) stay
 * transparent.
 */
function drawCrop(image: HTMLImageElement, crop: Crop, zoom: number, height: number): HTMLCanvasElement | null {
  const pixelCrop = convertToPixelCrop(crop, image.width, image.height);
  if (!pixelCrop.width || !pixelCrop.height || !image.naturalWidth) return null;
  const rect = cropToImageRect(pixelCrop, { width: image.width, height: image.height }, zoom);
  const toNatural = image.naturalWidth / image.width;
  const sx = rect.x * toNatural;
  const sy = rect.y * toNatural;
  const sw = rect.width * toNatural;
  const sh = rect.height * toNatural;
  const canvas = document.createElement('canvas');
  canvas.height = height;
  canvas.width = Math.max(1, Math.round(height * (sw / sh)));
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.imageSmoothingQuality = 'high';
  // The whole image, positioned so the crop area fills the canvas.
  const k = canvas.height / sh;
  ctx.drawImage(image, -sx * k, -sy * k, image.naturalWidth * k, image.naturalHeight * k);
  return canvas;
}

export function LogoCropper({ open, onOpenChange, imageSrc, onCropComplete }: LogoCropperProps) {
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState(1);
  const imgRef = useRef<HTMLImageElement>(null);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const onImageLoad = useCallback(() => {
    setCrop(FULL_CROP);
  }, []);

  const resetCrop = () => {
    setCrop(FULL_CROP);
    setScale(1);
  };

  // Live preview of the saved logo at website-header height.
  useEffect(() => {
    const image = imgRef.current;
    if (!image || !crop) return;
    const timer = window.setTimeout(() => {
      const canvas = drawCrop(image, crop, scale, 144);
      setPreviewUrl(canvas ? canvas.toDataURL('image/png') : null);
    }, 80);
    return () => window.clearTimeout(timer);
  }, [crop, scale]);

  const handleCropComplete = async () => {
    const image = imgRef.current;
    if (!image) return;
    // The box as it is now (also when it was never dragged).
    const current = crop ?? completedCrop;
    if (!current) return;
    const canvas = drawCrop(image, current, scale, OUTPUT_HEIGHT);
    if (!canvas) return;

    canvas.toBlob(
      (blob) => {
        if (blob) {
          onCropComplete(blob);
          onOpenChange(false);
        }
      },
      'image/png',
      1
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92dvh] max-w-2xl flex-col">
        <DialogHeader>
          <DialogTitle>Crop Your Logo</DialogTitle>
        </DialogHeader>

        {/* Scrolls on short screens so the Save button below always stays in view. */}
        <div className="-mx-1 min-h-0 flex-1 space-y-4 overflow-y-auto px-1">
          <p className="text-sm text-muted-foreground">
            Move and resize the box around your logo. Exactly what's inside the box is what appears on your website.
          </p>

          <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/50 px-3 py-2">
            <ZoomOut className="h-4 w-4 text-muted-foreground" />
            <Slider
              value={[scale]}
              onValueChange={(values) => setScale(values[0])}
              min={0.5}
              max={3}
              step={0.1}
              className="flex-1"
            />
            <ZoomIn className="h-4 w-4 text-muted-foreground" />
            <Label className="adm-num min-w-[44px] text-right text-[12.5px] font-medium text-muted-foreground">
              {Math.round(scale * 100)}%
            </Label>
            <Button variant="ghost" size="sm" onClick={resetCrop} aria-label="Reset crop" className={adminButton.iconGhost}>
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>

          <div className="adm-stripes flex justify-center overflow-auto rounded-xl border border-border p-4">
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              minHeight={20}
              minWidth={20}
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Crop preview"
                // Inline: react-image-crop's own CSS overrides a max-height class.
                style={{ transform: `scale(${scale})`, transformOrigin: 'center', maxHeight: 'min(360px, 34dvh)' }}
                onLoad={onImageLoad}
                className="w-auto max-w-full"
              />
            </ReactCrop>
          </div>

          <div className="text-xs text-muted-foreground text-center">
            Drag the corners to resize, drag inside to move
          </div>

          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">How it will look in your website header</p>
            <div className="flex h-[88px] items-center rounded-xl border border-border bg-white px-5">
              {previewUrl ? (
                <img src={previewUrl} alt="Logo preview" className="h-[72px] w-auto max-w-[260px] object-contain" />
              ) : (
                <span className="text-xs text-muted-foreground">Preview appears here</span>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0 border-t border-border pt-4">
          <Button variant="outline" size="sm" className={adminButton.outline} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" className={adminButton.primary} onClick={handleCropComplete}>
            Save Logo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}